import { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AllAnalogues,
  DrugItem,
  DrugReview,
  PriceHistory
} from 'src/api/types';
import {
  generateCatalogPageLink,
  generateFirstLevelCategoryPageLink,
  generateSecondLevelCategoryPageLink,
  generateThirdLevelCategoryPageLink
} from 'src/utils/links';
import useViewedDrugsStore from 'src/hooks/useViewedDrugsStore';
import useSSRMultyLevelCategoriesStore from 'src/hooks/useSSRMultyLevelCategoriesStore';

import Skeleton from 'react-loading-skeleton';
import dynamic from 'next/dynamic';
import { delayWrapper } from 'src/utils/delayWrapper';
import Page from '../../shared-components/Page/Page';
import trns from './trns';

const BreadCrumbs = dynamic(
  () =>
    delayWrapper(
      import('src/shared-components/design-system/BreadCrumbs').then(
        (file) => file.BreadCrumbs
      )
    ),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

const DrugInfoTabs = dynamic(
  () => delayWrapper(import('./components/DrugInfoTabs')),
  {
    loading: () => <Skeleton width={'100%'} height={600} />
  }
);

interface Props {
  drugData: DrugItem | null;
  analoguesList: AllAnalogues | null;
  priceHistory: PriceHistory[] | null;
  // otherPopularDrugsList: DrugItem[] | null;
  reviewsList: DrugReview[];
}

const DrugContainer: FC<Props> = ({
  drugData,
  analoguesList,
  // otherPopularDrugsList,
  priceHistory,
  reviewsList
}) => {
  const { multyLevelCategoriesList } = useSSRMultyLevelCategoriesStore();
  const { setViewedDrug } = useViewedDrugsStore();

  const selectedFirstLevelCategory = multyLevelCategoriesList?.find(
    (subC) =>
      subC?.translitName ===
      (drugData.categories.firstLevel?.translitName as string)
  );

  useEffect(() => {
    if (drugData && drugData?.translitName !== '') {
      setViewedDrug(drugData?.translitName);
    }
  }, [drugData?.translitName, setViewedDrug]);

  const breadCrumbsItems = [
    {
      id: 'home',
      title: <FormattedMessage {...trns.homeBreadCrumb} />,
      href: '/'
    },
    {
      id: 'catalog',
      title: <FormattedMessage {...trns.catalogBreadCrumb} />,
      href: generateCatalogPageLink()
    },
    {
      id: drugData.categories.firstLevel?.translitName || '',
      title: <span>{drugData.categories.firstLevel?.name || ''}</span>,
      href: generateFirstLevelCategoryPageLink({
        category: selectedFirstLevelCategory
      })
    },
    {
      id: drugData.categories.secondLevel?.translitName || '',
      title: <span>{drugData.categories.secondLevel?.name || ''}</span>,
      href: generateSecondLevelCategoryPageLink({
        category: selectedFirstLevelCategory,
        subCategoryId: drugData.categories.secondLevel?.translitName
      })
    },
    {
      id: drugData.categories.thirdLevel?.translitName || '',
      title: <span>{drugData.categories.thirdLevel?.name || ''}</span>,
      href: generateThirdLevelCategoryPageLink({
        category: selectedFirstLevelCategory,
        secondCategoryId: drugData.categories.secondLevel?.translitName,
        thirdCategoryId: drugData.categories.thirdLevel?.translitName
      })
    },
    drugData.tradeNameLink.translitName
      ? {
          id: 'tradeName',
          title: drugData.tradeName,
          href: `/tradename/${drugData.tradeNameLink.translitName}`
        }
      : null,
    {
      id: 'drug',
      title: drugData.name
    }
  ].filter((item) => !!item);

  // save drug id to cookies on mount for ViewedDrugsContainer
  return (
    <Page toTopButtonProps={{ classNames: 'lm:bottom-8' }}>
      <BreadCrumbs items={breadCrumbsItems} />
      <DrugInfoTabs
        drug={drugData}
        analoguesList={analoguesList}
        // otherPopularDrugsList={otherPopularDrugsList}
        thirdCategoryName={drugData.categories.thirdLevel?.name}
        priceHistory={priceHistory}
        reviewsList={reviewsList}
      />
    </Page>
  );
};

export default DrugContainer;
